// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {stringFilterMatches} from 'common/util/string';
import {defaultOrdering, mapArguments} from 'common/util/function';
import {SectionItem} from 'common/components/items-menu/types';

import css from './index.inline.styl';
import {template} from './template';

const MIN_INPUT_WIDTH = `12px`;

export type TagSelectorAttrs = {
  'all-tags': string[];
  error: boolean;
  'input-text': any;
  'max-tag-input-height': string;
  'max-tag-items-height': string;
  'min-tags': number;
  multiline: boolean;
  options: SectionItem[];
  'read-only': boolean;
  'read-only-tags': string[];
  saving: boolean;
  'selected-tags': string[];
  'static-tags': boolean;
};

export enum TagAction {
  AddTag = `addTag`,
  RemoveTag = `removeTag`,
  UpdateInput = `updateInput`,
}

export type TagsSaveEvent = CustomEvent<{
  tags: Set<string>;
}>;

export type TagsChangeEvent = CustomEvent<{
  tagName?: string;
  action: TagAction;
  inputText?: string;
  matchingTags?: Set<string>;
}>;

export type TagSelectorJsxProps = {
  attrs?: Partial<TagSelectorAttrs>;
  on?: {
    change?: (ev: TagsChangeEvent) => void;
    save?: (ev: CustomEvent) => void;
  };
};

type State = {
  activeTagIndex: number;
  options: SectionItem[];
  inputText: string;
  inputWidth: string;
  matchingTags: SectionItem[];
  selectedTags: Set<string>;
};

export class TagSelector extends Component<State, TagSelectorAttrs> {
  static get attrsSchema(): AttrsSchema<TagSelectorAttrs> {
    return {
      ...super.attrsSchema,
      'all-tags': {type: `json`, description: `DEPRECATED: interface:string[] list of non read only tags`},
      error: `boolean`,
      'input-text': `json`,
      'max-tag-input-height': {type: `string`, default: `280px`},
      'max-tag-items-height': {type: `string`, default: `280px`},
      'min-tags': {type: `number`, default: 1},
      multiline: `boolean`,
      options: {type: `json`, description: `interface: SectionItem[]`},
      'read-only': `boolean`,
      'read-only-tags': {
        type: `json`,
        description: `DEPRECATED: interface:string[] list of read only tags. Use options with isDisabled item property instead`,
      },
      saving: `boolean`,
      'selected-tags': {
        type: `json`,
        description: `DEPRECATED: interface:string[] list of tags to be preselected (displayed in the input box). Use options with isSelected item property instead`,
      },
      'static-tags': {
        type: `boolean`,
        description: `Remove capability to add new tags but keep component editable ("read-only" removes save button)`,
      },
    };
  }

  get config() {
    return {
      defaultState: {
        activeTagIndex: -1,
        options: [],
        inputText: ``,
        inputWidth: MIN_INPUT_WIDTH,
        matchingTags: [],
        selectedTags: new Set<string>(),
      },
      css,
      template,
      helpers: {
        showCreateButton: () => {
          const isNewTag =
            this.state.inputText && !this.state.options.filter((tag) => tag.value === this.state.inputText).length;
          return isNewTag && !this.attr(`read-only`) && !this.attr(`static-tags`);
        },
        addTag: ({item, modifierKeys}: any) => {
          if (!modifierKeys.Alt) {
            this._addTag(item);
          }
        },
        addNewTag: (tag: string) => this._addNewTag(tag),
        removeTag: (tag: SectionItem) => this._removeTag(tag),
        saveTags: () => {
          if (!this.attr(`saving`)) {
            this.dispatchEvent(new CustomEvent(`save`, {detail: {tags: this.state.selectedTags}}));
          }
        },
        getNumTags: () => this.state.selectedTags.size,
        getSelectedTagAttrs: (): any => {
          return Array.from(this.state.selectedTags).map((tagName) => {
            const tag = this._getTagFromOptions(tagName);
            return {
              label: tag.label,
              isDisabled: tag.isDisabled,
              value: tag.value,
              attrs: {
                icon: tag.icon,
                'tag-name': tag.value,
              },
            };
          });
        },
        focusInput: () => this.focus(),
        handleInputChange: (ev: any) => {
          switch (ev.detail.action) {
            case `removeTag`:
              this._removeTag(ev.detail.tag);
              break;
            case `text`:
              this.update({inputText: ev.detail.value});
              this._filterTags();
              break;
          }
          this.dispatchEvent(
            new CustomEvent(`change`, {
              detail: {
                action: `updateInput`,
                inputText: this.state.inputText,
                matchingTags: this.state.matchingTags.map((tag) => tag.value),
              },
            }),
          );
        },
        handleKeyDown: (ev: any) => {
          if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
            if (ev.altKey) {
              this.config.helpers.saveTags();
            } else if (this.state.matchingTags.length === 0) {
              this._addNewTag(this.state.inputText);
            }
          }
        },
        getKeydownProxyEl: (): HTMLElement => this.el.querySelector(`.input-el`) || this.el,
        searchText: () => {
          return this._inputText(`search`, `Search boards...`);
        },
        emptyText: () => {
          return this._inputText(`empty`, `No boards found.`);
        },
        createNewText: () => {
          return this._inputText(`create`, `Create new board for:`);
        },
        getMenuSections: () => [{items: this.state.matchingTags}],
      },
      useShadowDom: true,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this._readTags();
    this._filterTags();
  }

  attributeChangedCallback(attr: keyof TagSelectorAttrs, oldValue: any, newValue: any) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (this.initialized) {
      switch (attr) {
        case `all-tags`:
        case `options`:
        case `read-only-tags`:
        case `selected-tags`:
          this.update({options: this._getOptions()});
          this._filterTags();
          break;
      }
    }
  }

  _readTags() {
    this.update({
      options: this._getOptions(),
    });
  }

  _getTagIfExists(tag: SectionItem) {
    const currentTag = this.state.options.find((t) => t.value === tag.value);
    return currentTag ? {...currentTag} : tag;
  }

  _selectTag(tag: SectionItem) {
    this.state.selectedTags.add(tag.value);
    const newTag = this._getTagIfExists(tag);
    newTag.isSelected = true;
    this._updateOptionsTag(newTag);
  }

  _deselectTag(tag: SectionItem) {
    this.state.selectedTags.delete(tag.value);
    const newTag = this._getTagIfExists(tag);
    newTag.isSelected = false;
    this._updateOptionsTag(newTag);
  }

  _getSelectedTags() {
    return Array.from(this.state.options).filter((tag) => tag.isSelected);
  }

  _getOptions() {
    let options: SectionItem[] = [];
    if (this.attr(`all-tags`)) {
      // Deprecated path: We used to use the `all-tags` attr and combine it with `selected-tags` and `read-only-tags`
      // but decided it was better to just have one `options` attr that combines all of that into one object.
      // We can delete this entire if-block once the codebase no longer uses all-tags, along with said attrs
      const readonlyTags = new Set(this.attr(`read-only-tags`));
      this.update({selectedTags: new Set(this.attr(`selected-tags`))});
      options = this.attr(`all-tags`)
        .sort(mapArguments(defaultOrdering, (a) => a.toLowerCase()))
        .map((tag) => {
          return {
            isDisabled: readonlyTags.has(tag),
            isSelected: this.state.selectedTags.has(tag),
            label: tag,
            value: tag,
          };
        });
    } else if (this.attr(`options`)) {
      this.update({
        selectedTags: new Set(
          this.attr(`options`)
            .filter((option) => option.isSelected)
            .map((option) => option.value || option.label),
        ),
      });
      this.attr(`options`).forEach((tag) => {
        tag.value = tag.value || tag.label;
        options.push(tag);
      });
    } else {
      // Do nothing if nothing was passed in
    }
    return options;
  }

  _filterTags() {
    const search = this.state.inputText.trim().toLowerCase();
    //Here to support deprecated functionality of return matchingTags in the updateInput event
    this.state.matchingTags = Array.from(this.state.options).filter(
      (tag) =>
        !tag.isSelected && (stringFilterMatches(tag.value, search) || stringFilterMatches(tag.label as string, search)),
    );
    this.update();
  }

  _removeTag(tag: SectionItem) {
    this._deselectTag(tag);
    this.update({inputText: ``, inputWidth: MIN_INPUT_WIDTH});
    this._filterTags();
    requestAnimationFrame(() => this.focus());
    this.dispatchEvent(new CustomEvent(`change`, {detail: {tagName: tag.value, action: `removeTag`}}));
  }

  _addNewTag(tagName: string) {
    const tag = {label: tagName, value: tagName};
    this._addTag(tag);
  }

  _addTag(tag: SectionItem) {
    if (tag.value && !tag.isSelected) {
      this._selectTag(tag);
      this.update({inputText: ``, inputWidth: MIN_INPUT_WIDTH});
      this._filterTags();
      requestAnimationFrame(() => this.focus());
      this.dispatchEvent(new CustomEvent(`change`, {detail: {tagName: tag.value, action: `addTag`}}));
    }
  }

  _inputText(field: string, defaultText: string) {
    const inputText = this.attr(`input-text`);
    return inputText && inputText[field] ? inputText[field] : defaultText;
  }

  _getTagFromOptions(value: string): SectionItem {
    return this.state.options.find((tag) => tag.value === value)!;
  }

  _updateOptionsTag(newTag: SectionItem) {
    let newTagAdded = false;
    const newOptions = this.state.options.map((tag) => {
      if (tag.value === newTag.value) {
        newTagAdded = true;
        return newTag;
      }
      return tag;
    });

    if (!newTagAdded) {
      newOptions.push(newTag);
    }

    this.update({options: newOptions});
  }
}

export const TagSelectorEl = defineAndMakeJsxFactory<TagSelectorJsxProps>(`mp-tag-selector`, TagSelector);
export const TagSelectorReact = panel2react<TagSelectorJsxProps>(`mp-tag-selector`, TagSelector);
